import { html, nothing } from "lit";
import type { LocalResourcesHistoryPoint, LocalResourcesSnapshot } from "../types.ts";

export type LlmStatsCapacityProps = {
  loading: boolean;
  error: string | null;
  snapshot: LocalResourcesSnapshot | null;
  history: LocalResourcesHistoryPoint[];
  autoRefresh: boolean;
  connected: boolean;
  onRefresh: () => void;
  onAutoRefreshChange: (next: boolean) => void;
  onOpenLocalLlm?: () => void;
};

function fmtBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
}

function fmtTime(ms: number | null | undefined): string {
  if (!ms) return "—";
  try {
    return new Date(ms).toLocaleTimeString();
  } catch {
    return "—";
  }
}

function fmtRelative(ms: number | null | undefined): string {
  if (!ms) return "—";
  const diff = Date.now() - ms;
  if (diff < 60000) return "just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
}

function colorForPercent(pct: number): string {
  if (pct >= 90) return "var(--danger)";
  if (pct >= 75) return "var(--warn)";
  return "var(--accent)";
}

function progressStyle(pct: number): string {
  return `width: ${Math.min(100, Math.max(0, pct))}%; background: ${colorForPercent(pct)};`;
}

function renderSparkline(
  history: LocalResourcesHistoryPoint[],
  field: "cpuPercent" | "memUsedPercent",
) {
  if (history.length < 2) {
    return html`
      <span style="color: var(--muted); font-size: 11px">Collecting…</span>
    `;
  }
  const values = history.map((p) => p[field]);
  const max = Math.max(1, ...values);
  const w = 120;
  const h = 32;
  const step = w / Math.max(1, values.length - 1);
  const points = values
    .map((v, i) => `${(i * step).toFixed(1)},${(h - (v / max) * h).toFixed(1)}`)
    .join(" ");
  return html`
    <svg class="llm-stats-capacity-sparkline" width=${w} height=${h} viewBox="0 0 ${w} ${h}" aria-hidden="true">
      <polyline fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" points=${points} />
    </svg>
  `;
}

function renderGaugeRing(value: number, max: number, label: string, sub: string) {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  const color = colorForPercent(pct);
  const r = 24;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;

  return html`
    <div class="capacity-gauge-card">
      <div class="capacity-gauge-ring">
        <svg width="60" height="60" viewBox="0 0 60 60">
          <circle cx="30" cy="30" r="${r}" fill="none" stroke="var(--bg-hover)" stroke-width="5" />
          <circle
            cx="30" cy="30" r="${r}"
            fill="none"
            stroke="${color}"
            stroke-width="5"
            stroke-linecap="round"
            stroke-dasharray="${circ}"
            stroke-dashoffset="${offset}"
            transform="rotate(-90 30 30)"
            style="transition: stroke-dashoffset 0.6s var(--ease-out); filter: drop-shadow(0 0 4px ${color}40);"
          />
        </svg>
        <div class="capacity-gauge-value" style="color:${color}">${Math.round(pct)}%</div>
      </div>
      <div class="capacity-gauge-label">${label}</div>
      <div class="capacity-gauge-sub">${sub}</div>
    </div>
  `;
}

export function renderLlmStatsCapacity(props: LlmStatsCapacityProps) {
  const snap = props.snapshot;
  const est = snap?.estimate;
  const host = snap?.host;
  const ollama = snap?.ollama;
  const gateway = snap?.gateway;

  return html`
    <section class="card llm-stats-capacity">
      <div class="llm-stats-section-header">
        <div class="llm-stats-section-title">
          <div class="llm-stats-section-icon">⬡</div>
          Local Capacity
        </div>
        <div class="llm-stats-section-actions">
          <label class="llm-stats-refresh-toggle" title="Refresh every 60 seconds while this tab is open">
            <input
              type="checkbox"
              .checked=${props.autoRefresh}
              @change=${(e: Event) => props.onAutoRefreshChange((e.target as HTMLInputElement).checked)}
              style="display:none"
            />
            <span class="toggle-pill ${props.autoRefresh ? "active" : ""}">
              ${props.autoRefresh ? "↻ Auto-refresh on" : "Auto-refresh off"}
            </span>
          </label>
          <button class="btn primary btn-sm" ?disabled=${!props.connected || props.loading} @click=${props.onRefresh}>
            ${props.loading ? "Refreshing…" : "Refresh now"}
          </button>
          ${
            props.onOpenLocalLlm
              ? html`
                  <button class="btn btn-sm" type="button" title="Open read-only Ollama activity console in Chat" @click=${props.onOpenLocalLlm}>
                    Local LLM console
                  </button>
                `
              : nothing
          }
        </div>
      </div>

      ${
        props.error
          ? html`
              <div class="llm-stats-callout danger" style="margin-bottom:14px;">
                <span class="llm-stats-callout-icon">⚠</span>
                <div>
                  <div class="llm-stats-callout-text">Failed to load capacity</div>
                  <div class="llm-stats-callout-sub">${props.error}</div>
                </div>
              </div>
            `
          : nothing
      }

      ${
        est
          ? html`
              <div class="llm-stats-callout ${est.suggestedAdditionalChats === 0 ? "warn" : "success"}">
                <span class="llm-stats-callout-icon">${est.suggestedAdditionalChats === 0 ? "⚠" : "✓"}</span>
                <div>
                  <div class="llm-stats-callout-text">${est.label}</div>
                  ${
                    est.bottlenecks.length
                      ? html`<div class="llm-stats-callout-bottlenecks">Bottlenecks: ${est.bottlenecks.join(", ")}</div>`
                      : nothing
                  }
                </div>
              </div>
            `
          : nothing
      }

      ${
        host && gateway && ollama
          ? html`
              <div class="capacity-gauges-row">
                ${renderGaugeRing(host.cpuPercent ?? 0, 100, "CPU", `${host.loadAvg[0].toFixed(2)} load · ${host.cpuCount} cores`)}
                ${renderGaugeRing(host.memUsedPercent ?? 0, 100, "Memory", `${fmtBytes(host.memUsedBytes)} / ${fmtBytes(host.memTotalBytes)}`)}
                ${renderGaugeRing(ollama.loadedModels.length, Math.max(1, ollama.installedModels.length), "Models loaded", `${ollama.loadedModels.length} of ${ollama.installedModels.length} installed`)}
                ${renderGaugeRing(gateway.activeRuns ?? 0, Math.max(1, gateway.mainMaxConcurrent ?? 1), "Agent runs", `${gateway.activeRuns} / ${gateway.mainMaxConcurrent} main slots`)}
              </div>

              <div class="capacity-progress-row">
                <div class="capacity-progress-item">
                  <div class="capacity-progress-label">
                    <span>CPU Usage</span>
                    <strong>${host.cpuPercent}%</strong>
                  </div>
                  <div class="capacity-progress-bar">
                    <div class="capacity-progress-fill" style="${progressStyle(host.cpuPercent ?? 0)}"></div>
                  </div>
                  <div style="font-size:11px;color:var(--muted);">Load: ${host.loadAvg[0].toFixed(2)} · ${host.cpuCount} cores</div>
                </div>
                <div class="capacity-progress-item">
                  <div class="capacity-progress-label">
                    <span>Memory</span>
                    <strong>${host.memUsedPercent}%</strong>
                  </div>
                  <div class="capacity-progress-bar">
                    <div class="capacity-progress-fill" style="${progressStyle(host.memUsedPercent ?? 0)}"></div>
                  </div>
                  <div style="font-size:11px;color:var(--muted);">${fmtBytes(host.memUsedBytes)} used of ${fmtBytes(host.memTotalBytes)}</div>
                </div>
              </div>

              <div class="capacity-sparkline-row">
                <div class="capacity-sparkline-item">
                  <div style="font-size:11px;color:var(--muted);margin-bottom:4px;">CPU history (24h)</div>
                  ${renderSparkline(props.history, "cpuPercent")}
                </div>
                <div class="capacity-sparkline-item">
                  <div style="font-size:11px;color:var(--muted);margin-bottom:4px;">Memory history (24h)</div>
                  ${renderSparkline(props.history, "memUsedPercent")}
                </div>
              </div>

              <div class="capacity-ollama-section">
                <div class="capacity-ollama-header">
                  <div class="capacity-ollama-status ${ollama.reachable ? "online" : "offline"}">
                    <span class="status-dot"></span>
                    Ollama ${ollama.reachable ? "reachable" : "unreachable"}
                  </div>
                  ${
                    ollama.version
                      ? html`<span class="llm-stats-mono" style="font-size:11px;color:var(--muted);">v${ollama.version}</span>`
                      : nothing
                  }
                </div>

                <div class="capacity-ollama-meta">
                  <span>${ollama.installedModels.length} installed</span>
                  <span>·</span>
                  <span>${ollama.loadedModels.length} loaded</span>
                  ${
                    ollama.baseUrl
                      ? html`<span>·</span><span class="llm-stats-mono" style="font-size:11px;">${ollama.baseUrl}</span>`
                      : nothing
                  }
                </div>

                ${
                  ollama.ollamaNumParallel != null || ollama.ollamaMaxLoadedModels != null
                    ? html`
                        <div class="capacity-ollama-env">
                          ${
                            ollama.ollamaNumParallel != null
                              ? html`<span class="env-tag">OLLAMA_NUM_PARALLEL=${ollama.ollamaNumParallel}</span>`
                              : nothing
                          }
                          ${
                            ollama.ollamaMaxLoadedModels != null
                              ? html`<span class="env-tag">OLLAMA_MAX_LOADED_MODELS=${ollama.ollamaMaxLoadedModels}</span>`
                              : nothing
                          }
                        </div>
                      `
                    : nothing
                }

                ${
                  ollama.error && ollama.reachable
                    ? html`<div class="llm-stats-callout warn" style="margin-top:10px;">
                        <span class="llm-stats-callout-icon">⚠</span>
                        <div>
                          <div class="llm-stats-callout-text">Ollama warning</div>
                          <div class="llm-stats-callout-sub">${ollama.error}</div>
                        </div>
                      </div>`
                    : nothing
                }

                <table class="llm-stats-model-table">
                  <thead>
                    <tr>
                      <th>Loaded model</th>
                      <th>Size</th>
                      <th>VRAM (reported)</th>
                      <th>Expires</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${
                      ollama.loadedModels.length > 0
                        ? ollama.loadedModels.map(
                            (model) => html`
                              <tr>
                                <td class="llm-stats-mono" style="font-weight:600;">${model.name}</td>
                                <td class="llm-stats-mono">${fmtBytes(model.size)}</td>
                                <td class="llm-stats-mono">${model.sizeVram != null ? fmtBytes(model.sizeVram) : "—"}</td>
                                <td class="llm-stats-muted">${model.expiresAt ?? "—"}</td>
                              </tr>
                            `,
                          )
                        : html`
                            <tr>
                              <td colspan="4">
                                <div class="llm-stats-empty">
                                  <div class="llm-stats-empty-icon">⬡</div>
                                  <div class="llm-stats-empty-title">
                                    ${
                                      ollama.reachable
                                        ? "No models loaded in Ollama right now"
                                        : "Cannot reach Ollama"
                                    }
                                  </div>
                                  <div class="llm-stats-empty-desc">
                                    ${
                                      ollama.reachable
                                        ? "Load a model with `ollama run <model>` to see it here."
                                        : "Check that Ollama is running on this host."
                                    }
                                  </div>
                                </div>
                              </td>
                            </tr>
                          `
                    }
                  </tbody>
                </table>
              </div>

              <div class="llm-stats-muted" style="font-size:11px;margin-top:12px;display:flex;align-items:center;gap:6px;">
                <span>Last updated ${fmtRelative(snap?.collectedAtMs)}</span>
                <span>·</span>
                <span>${props.history.length} history points collected</span>
              </div>
            `
          : !props.loading && !props.error
            ? html`
                <div class="llm-stats-empty">
                  <div class="llm-stats-empty-icon">⬡</div>
                  <div class="llm-stats-empty-title">No capacity data yet</div>
                  <div class="llm-stats-empty-desc">
                    Connect to the gateway to load host CPU/RAM and Ollama headroom.
                  </div>
                </div>
              `
            : props.loading
              ? html`
                  <div class="llm-stats-loading">
                    <div class="llm-stats-spinner"></div>
                    Loading local capacity…
                  </div>
                `
              : nothing
      }
    </section>

    <style>
      .capacity-gauges-row {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
        gap: 12px;
        margin: 14px 0;
      }
      .capacity-gauge-card {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
        padding: 14px;
        border: 1px solid var(--border);
        border-radius: 12px;
        background: var(--bg-elevated);
        transition: border-color 0.15s, box-shadow 0.15s;
      }
      .capacity-gauge-card:hover { border-color: var(--border-strong); box-shadow: var(--shadow-md); }
      .capacity-gauge-ring { position: relative; width: 64px; height: 64px; }
      .capacity-gauge-value {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 13px;
        font-weight: 800;
        font-family: var(--mono);
        letter-spacing: -0.02em;
      }
      .capacity-gauge-label { font-size: 12px; font-weight: 700; color: var(--text); }
      .capacity-gauge-sub { font-size: 10px; color: var(--muted); text-align: center; line-height: 1.4; }
      .capacity-progress-row {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: 16px;
        margin-top: 14px;
        padding: 16px;
        background: var(--bg-accent);
        border-radius: 12px;
        border: 1px solid var(--border);
      }
      .capacity-progress-item { display: flex; flex-direction: column; gap: 6px; }
      .capacity-progress-label {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 12px;
        color: var(--muted);
      }
      .capacity-progress-label strong { color: var(--text); font-family: var(--mono); font-size: 13px; }
      .capacity-progress-bar { height: 6px; background: var(--bg-hover); border-radius: 999px; overflow: hidden; }
      .capacity-progress-fill {
        height: 100%;
        border-radius: 999px;
        position: relative;
        transition: width 0.6s var(--ease-out);
      }
      .capacity-sparkline-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;
        margin-top: 12px;
      }
      .capacity-sparkline-item { padding: 12px; background: var(--bg-accent); border: 1px solid var(--border); border-radius: 10px; }
      .capacity-ollama-section {
        margin-top: 16px;
        padding: 16px;
        background: var(--bg-accent);
        border-radius: 12px;
        border: 1px solid var(--border);
      }
      .capacity-ollama-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 8px;
      }
      .capacity-ollama-status {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        font-size: 12px;
        font-weight: 600;
        padding: 4px 10px;
        border-radius: 999px;
        border: 1px solid;
      }
      .capacity-ollama-status.online { color: var(--ok); background: var(--ok-subtle); border-color: rgba(34, 197, 94, 0.25); }
      .capacity-ollama-status.offline { color: var(--muted); background: var(--bg-hover); border-color: var(--border); }
      .status-dot { width: 7px; height: 7px; border-radius: 50%; background: currentColor; }
      .capacity-ollama-status.online .status-dot { animation: pulse-dot 2s ease-in-out infinite; }
      @keyframes pulse-dot { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
      .capacity-ollama-meta {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 12px;
        color: var(--muted);
        margin-bottom: 8px;
        flex-wrap: wrap;
      }
      .capacity-ollama-env { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px; }
      .env-tag {
        font-family: var(--mono);
        font-size: 11px;
        padding: 3px 8px;
        background: var(--bg-hover);
        border: 1px solid var(--border);
        border-radius: 6px;
        color: var(--muted);
      }
      .llm-stats-refresh-toggle { cursor: pointer; }
      .toggle-pill {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 5px 12px;
        border-radius: 999px;
        border: 1px solid var(--border);
        font-size: 12px;
        font-weight: 600;
        background: var(--bg-elevated);
        color: var(--muted);
        cursor: pointer;
        transition: all 0.15s;
        user-select: none;
      }
      .toggle-pill:hover { border-color: var(--border-strong); color: var(--text); }
      .toggle-pill.active { background: var(--ok-subtle); border-color: rgba(34, 197, 94, 0.3); color: var(--ok); }
      .llm-stats-model-table {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        border-radius: 10px;
        overflow: hidden;
        border: 1px solid var(--border);
        background: var(--bg-elevated);
        margin-top: 10px;
      }
      .llm-stats-model-table th, .llm-stats-model-table td {
        padding: 9px 12px;
        text-align: left;
        font-size: 12px;
        border-bottom: 1px solid var(--border);
      }
      .llm-stats-model-table th {
        background: var(--bg-accent);
        font-size: 10px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.07em;
        color: var(--muted);
      }
      .llm-stats-model-table tbody tr:hover { background: var(--bg-hover); }
      .llm-stats-model-table tbody tr:last-child td { border-bottom: none; }
      .llm-stats-section-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        flex-wrap: wrap;
      }
      .llm-stats-section-title {
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 16px;
        font-weight: 700;
        letter-spacing: -0.02em;
        color: var(--text-strong);
      }
      .llm-stats-section-icon {
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 8px;
        background: var(--accent-subtle);
        color: var(--accent);
        font-size: 15px;
        flex-shrink: 0;
      }
      .llm-stats-section-actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
      .llm-stats-mono { font-family: var(--mono); }
      .llm-stats-muted { color: var(--muted); }
      .llm-stats-callout {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 12px 14px;
        border-radius: 10px;
        border: 1px solid;
        margin-top: 14px;
        animation: scale-in 0.2s ease both;
      }
      .llm-stats-callout.success { background: var(--ok-subtle); border-color: rgba(34, 197, 94, 0.3); color: var(--ok); }
      .llm-stats-callout.warn { background: var(--warn-subtle); border-color: rgba(245, 158, 11, 0.3); color: var(--warn); }
      .llm-stats-callout.danger { background: var(--danger-subtle); border-color: rgba(239, 68, 68, 0.3); color: var(--danger); }
      .llm-stats-callout-icon { font-size: 16px; flex-shrink: 0; margin-top: 1px; }
      .llm-stats-callout-text { font-size: 13px; font-weight: 600; }
      .llm-stats-callout-sub { font-size: 11px; opacity: 0.8; margin-top: 4px; }
      .llm-stats-callout-bottlenecks { font-size: 11px; opacity: 0.75; margin-top: 6px; }
      .llm-stats-loading {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 40px 24px;
        gap: 12px;
        color: var(--muted);
        font-size: 13px;
      }
      .llm-stats-spinner {
        width: 22px;
        height: 22px;
        border: 2px solid var(--border);
        border-top-color: var(--accent);
        border-radius: 50%;
        animation: spin 0.7s linear infinite;
      }
      @keyframes spin { to { transform: rotate(360deg); } }
      @keyframes scale-in { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
      .llm-stats-empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 36px 24px;
        text-align: center;
        gap: 8px;
      }
      .llm-stats-empty-icon { font-size: 32px; opacity: 0.25; }
      .llm-stats-empty-title { font-size: 13px; font-weight: 600; color: var(--text); }
      .llm-stats-empty-desc { font-size: 12px; color: var(--muted); max-width: 260px; line-height: 1.5; }
      .llm-stats-capacity-sparkline { display: block; color: var(--accent); opacity: 0.7; }
      @media (max-width: 768px) {
        .capacity-gauges-row { grid-template-columns: repeat(2, 1fr); }
        .capacity-sparkline-row { grid-template-columns: 1fr; }
      }
      @media (max-width: 480px) {
        .llm-stats-section-header { flex-direction: column; align-items: flex-start; }
        .capacity-gauges-row { grid-template-columns: repeat(2, 1fr); }
      }
      .btn-sm {
        height: 30px;
        padding: 0 12px;
        font-size: 12px;
        border-radius: 8px;
        border: 1px solid var(--border);
        background: var(--bg-elevated);
        color: var(--text);
        font-weight: 600;
        cursor: pointer;
        transition: all 0.12s;
      }
      .btn-sm:hover:not(:disabled) { background: var(--bg-hover); border-color: var(--border-strong); }
      .btn-sm.primary { background: var(--accent); border-color: var(--accent); color: white; }
      .btn-sm.primary:hover:not(:disabled) { background: var(--accent-hover); border-color: var(--accent-hover); }
      .btn-sm:disabled { opacity: 0.45; cursor: not-allowed; }
    </style>
  `;
}
